/**
 * URL-as-state worked example: `?poolId=` selects the pool screen.
 *
 * Two shapes live on this one route. Without a `poolId` it is the Pools
 * section: the section heading and the list. With a
 * `poolId` it is the pool itself, which takes over the viewport with its own
 * back arrow, title, share control and tab row (ISSUE-952). `AppShell` reads
 * the same condition through `chromelessRoutes.ts` and renders no header
 * there, so nothing competes with the pool for the top of the screen. The
 * four section bar still sits at the bottom at phone width (ISSUE-965).
 *
 * Both shapes still render through `SectionTwoPane`, so the desktop
 * list-plus-detail split at 1024 and above survives opening a pool: the pools
 * list stays in the left pane and the pool screen fills the right one,
 * exactly as every other section behaves. ISSUE-952 removed the SHELL header
 * from a pool screen and nothing more; dropping the two-pane split as well
 * would take a desktop layout nobody asked to lose. The section heading is
 * the one thing the pool shape leaves out, because R1's "nothing else
 * competing for the top of the screen" is about that row.
 *
 * The two entry dialogs, Create pool and Join a pool, mount in both shapes
 * (ISSUE-1034), because the list beside a pool at 1024 and up carries both
 * buttons.
 */
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { CreatePoolDialog } from "../features/createPool/CreatePoolDialog";
import { PoolMemberDetailPane } from "../features/poolMemberDetail/PoolMemberDetailPane";
import {
  closePoolMemberDetailSearch,
  isPoolMemberDetailOpen,
  openPoolMemberDetailSearch,
  readPoolMemberDetailTarget,
} from "../features/poolMemberDetail/poolMemberDetailRoute";
import { INVITE_SHEET_NAME } from "../features/poolInvite/inviteSheetRoute";
import { MEMBER_ACTIONS_SHEET_NAME } from "../features/poolMembers/poolMembersRoute";
import { closePoolNotificationsSearch } from "../features/poolNotifications/poolNotificationsRoute";
import { JoinPoolDialog } from "../features/pools/JoinPoolDialog";
import { openEnteredPoolSearch } from "../features/pools/poolsEntryRoute";
import { PoolsListPane } from "../features/pools/PoolsListPane";
import { isCreatePoolAvailable } from "../features/pools/poolsListState";
import { PoolScreenPane } from "../features/poolScreen/PoolScreenPane";
import {
  openPoolTabSearch,
  readPoolTab,
} from "../features/poolScreen/poolScreenRoute";
import { closeOverlay } from "../routeState/overlay";
import { validatePoolsSearch } from "../routeState/sectionSearch";
import { useOverlay } from "../routeState/useOverlay";
import { SectionEmptyState, SectionScreen } from "../shell/SectionScreen";
import { SectionTwoPane } from "../shell/SectionTwoPane";

const JOIN_POOL_DIALOG = "join-pool";

const PoolsScreen = (): ReactNode => {
  const search = useSearch({ from: "/pools" });
  const navigate = useNavigate({ from: "/pools" });
  const overlay = useOverlay();

  // ISSUE-782, Q-782-A answer B: entry to the create-pool wizard is closed
  // when no league is open for new pools, so a fan is never invited into a
  // flow that cannot finish. The gate sits here rather than on the button
  // alone, so `?dialog=create-pool` typed or restored from history cannot
  // reach the wizard either. `PoolsListPane` and `CreatePoolDialog` already
  // read this same query, so this is a cache hit, not a third round trip.
  const catalog = useLeagueCatalogGet({});
  const canCreatePool = isCreatePoolAvailable({
    isLoading: catalog.isLoading,
    hasFailed: catalog.isError || Boolean(catalog.data?.error),
    sports: catalog.data?.data?.sports ?? null,
  });

  // A replace, not a push, and the list's own `?q=` filter survives. Pushing
  // made the browser's own Back button walk straight back into the pool the
  // fan just left, and dropping `q` threw away the search that found it.
  const goBackToPoolsList = (): void => {
    void navigate({
      to: "/pools",
      search: (prev) => (prev.q === undefined ? {} : { q: prev.q }),
      replace: true,
    });
  };

  const poolsList = (
    <PoolsListPane
      query={search.q ?? ""}
      selectedPoolId={search.poolId}
      onQueryChange={(q) => {
        void navigate({
          search: (prev) => ({ ...prev, q: q === "" ? undefined : q }),
          replace: true,
        });
      }}
      onSelectPool={(poolId) => {
        void navigate({ search: (prev) => ({ ...prev, poolId }) });
      }}
      onCreatePool={() => {
        void navigate({
          search: (prev) => ({ ...prev, dialog: "create-pool" }),
        });
      }}
      onJoinPool={() => {
        void navigate({
          search: (prev) => ({ ...prev, dialog: JOIN_POOL_DIALOG }),
        });
      }}
      // ISSUE-199. A real navigation to `/pools/search`, not an overlay
      // on this route: search is its own screen on both clients, and a
      // fan expects the browser back button to return here from one.
      onFindPool={() => {
        void navigate({ to: "/pools/search" });
      }}
    />
  );

  // ISSUE-1034. Both entry dialogs mount in both shapes of this route. The
  // pools list sits beside an open pool at 1024 and up, and its Create pool
  // and Join a pool buttons only set ?dialog=, so a pool screen that did not
  // mount the dialogs left those buttons writing to the address bar and
  // nothing else.
  const entryDialogs = (
    <>
      <CreatePoolDialog
        open={search.dialog === "create-pool" && canCreatePool}
        onClose={() => {
          void navigate({
            search: (prev) => closeOverlay(prev, { kind: "dialog" }),
            replace: true,
          });
        }}
        onCreated={(poolId) => {
          void navigate({
            search: (prev) => openEnteredPoolSearch(prev, poolId),
            replace: true,
          });
        }}
        onInvitePeople={(poolId) => {
          void navigate({
            search: (prev) => ({
              ...openEnteredPoolSearch(prev, poolId),
              sheet: INVITE_SHEET_NAME,
            }),
            replace: true,
          });
        }}
      />
      <JoinPoolDialog
        open={overlay.isOpen("dialog", JOIN_POOL_DIALOG)}
        {...(search.joinCode === undefined
          ? {}
          : { initialJoinCode: search.joinCode })}
        onClose={() => {
          overlay.close({ kind: "dialog", clearParams: ["joinCode"] });
        }}
        onGoToPool={(poolId) => {
          void navigate({
            search: (prev) => openEnteredPoolSearch(prev, poolId),
          });
        }}
      />
    </>
  );

  if (search.poolId !== undefined) {
    // One member's pool-scoped profile is a drill-down over the Members tab
    // (ISSUE-194), not a fifth tab, so it still replaces the screen. It
    // replaces the DETAIL pane, not the window: on a desktop the pools list
    // stays beside it the way it does for every other pool address.
    const detail = isPoolMemberDetailOpen(search) ? (
      <PoolMemberDetailPane
        poolId={search.poolId}
        memberId={readPoolMemberDetailTarget(search) ?? ""}
        onGoBack={() => {
          void navigate({
            search: (prev) => closePoolMemberDetailSearch(prev),
            replace: true,
          });
        }}
        // The one manage control hands off to the actions dialog ISSUE-817
        // already wired on the roster, rather than mounting a second copy
        // of the permission table here. A push, not a replace, so the
        // browser back button returns to the profile.
        onManageMember={(memberId) => {
          void navigate({
            search: (prev) => ({
              ...closePoolMemberDetailSearch(prev),
              sheet: MEMBER_ACTIONS_SHEET_NAME,
              memberId,
            }),
          });
        }}
      />
    ) : (
      <PoolScreenPane
        poolId={search.poolId}
        tab={readPoolTab(search)}
        panel={search.panel ?? null}
        memberQ={search.memberQ ?? ""}
        contestId={search.contestId}
        // `replace: true` is what makes the browser's own Back button leave
        // the pool rather than walk back through every tab the fan visited
        // (R3). Opening the pool from the list is the pushed entry.
        onTabChange={(next) => {
          void navigate({
            search: (prev) => openPoolTabSearch(prev, next),
            replace: true,
          });
        }}
        onGoBack={goBackToPoolsList}
        onSearchQueryChange={(next) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              memberQ: next === "" ? undefined : next,
            }),
            replace: true,
          });
        }}
        onOpenMemberDetail={(userId) => {
          void navigate({
            search: (prev) => openPoolMemberDetailSearch(prev, userId),
          });
        }}
        onOpenChat={(chatId) => {
          // The pool chat is a conversation in Messages, not a screen of its
          // own: `/messages?conversationId=` is the same address a row in the
          // Messages list opens, so the two land on one pane rather than two
          // copies of it.
          void navigate({
            to: "/messages",
            search: { conversationId: chatId },
          });
        }}
        onClosePanel={() => {
          void navigate({
            search: (prev) => closePoolNotificationsSearch(prev),
            replace: true,
          });
        }}
      />
    );

    // No `SectionScreen` around this one: the "Pools" heading is exactly the
    // kind of thing R1 keeps off the top of a pool screen. The two-pane split
    // below it is not, and it stays.
    return (
      <>
        <SectionTwoPane
          list={poolsList}
          detail={detail}
          emptyDetail={<SectionEmptyState headline="Pick a pool to see it." />}
        />
        {entryDialogs}
      </>
    );
  }

  return (
    <SectionScreen heading="Pools">
      <SectionTwoPane
        list={poolsList}
        detail={null}
        emptyDetail={<SectionEmptyState headline="Pick a pool to see it." />}
      />
      {entryDialogs}
    </SectionScreen>
  );
};

export const Route = createFileRoute("/pools")({
  beforeLoad: requireSignedIn,
  validateSearch: validatePoolsSearch,
  component: PoolsScreen,
});
